import React, { useState, useEffect } from 'react';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { SettingsPage } from '../../components/SettingsPage/SettingsPage';
import { ToggleSettingCard } from '../../components/SettingCards/ToggleSetting/ToggleSetting';
import { RadioSetting } from '../../components/SettingCards/RadioSetting/RadioSetting';
import { ModalDialog, ModalType } from '../../components/ModalDialog/ModalDialog';
import { WORKSPACE_UPDATE_SETTING_IDS } from '../../config/SettingIds';
import { SettingsLogger, logConstants, AotCategory } from '../../utils/SettingsLogger';
import { GetSettingsResponse } from '../../Contracts/responses';
import { getFilteredSettingIds } from '../../config/DynamicSettingsFilter';
import { SettingsCategory } from '../../config/SettingsCategory';
import { SETTING_NOTE_MESSAGES } from '../../components/SettingCards/SettingCard/SettingStatus/SettingStatus';

import {
	getSettingConfig,
	RadioSettingConfig,
	ToggleSettingConfig,
} from '../../config/SettingsDefinitions';
import { PrimaryButton } from '../../components/Buttons/Buttons';
import './WorkspaceUpdateSettingsPage.css';

export type WorkspaceUpdateChannel = 'release' | 'beta' | 'dev' | 'canary';

interface WorkspaceUpdateSettingsPageProps {
	onNavigatingBack?: () => void;
	title: string;
	description?: string;
}

export const WorkspaceUpdateSettingsPage: React.FC<WorkspaceUpdateSettingsPageProps> = ({
	onNavigatingBack,
	title,
}) => {
	const [notifyUpdatesSetting, setNotifyUpdatesSetting] = useState({
		value: true,
		disabled: true,
	}); // Default value and start as disabled until loaded
	const [updateChannelSetting, setUpdateChannelSetting] = useState<{
		value: WorkspaceUpdateChannel;
		disabled: boolean;
	}>({
		value: 'release',
		disabled: true,
	}); // Default value and start as disabled until loaded

	const [channelConfigValue, setChannelConfigValue] = useState<number>(3); // Default to 3 (release+beta) for better test compatibility
	const [showChannelSwitchModal, setShowChannelSwitchModal] = useState(false);
	const [pendingChannelValue, setPendingChannelValue] =
		useState<WorkspaceUpdateChannel>('release');
	const [previousChannelValue, setPreviousChannelValue] =
		useState<WorkspaceUpdateChannel>('release');

	// Helper function to get modal content based on the selected channel
	const getModalContent = (channel: WorkspaceUpdateChannel) => {
		// Get the radio setting config to access actual channel options
		const baseConfig = getSettingConfig(
			WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL
		) as RadioSettingConfig;
		// Find the option for the selected channel
		const selectedOption = baseConfig.options?.find((option) => option.value === channel);

		if (selectedOption) {
			return {
				title: `Switch to ${selectedOption.label} channel?`,
				description:
					selectedOption.description ||
					`Switch to ${selectedOption.label} channel to get different update preferences.`,
			};
		}

		// Fallback for unknown channels
		return {
			title: 'Switch channel?',
			description: 'Are you sure you want to change the update channel?',
		};
	};
	const getAvailableChannels = (configValue: number) => {
		switch (configValue) {
			case 3:
				return ['release', 'beta'];
			case 7:
				return ['release', 'beta', 'dev'];
			case 15:
				return ['release', 'beta', 'dev', 'canary'];
			default:
				return ['release']; // Only release for unknown values
		}
	};

	const handleCheckForUpdates = async () => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: WORKSPACE_UPDATE_SETTING_IDS.TRIGGER_UPDATE_DOWNLOAD,
				settingValue: true,
			});
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				'Successfully triggered update download',
				AotCategory.Info,
				'Successfully triggered update download'
			);
		} catch (error) {
			const errorMessage = `Failed to trigger update download: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
		}
	};

	// Load settings on page load
	useEffect(() => {
		const loadWorkspaceUpdateSettings = async () => {
			try {
				const nativeBridge = SettingsNativeBridgeFactory.getBridge();

				// Get platform and entrypoint from bridge
				const platform = nativeBridge.getPlatform();
				const entrypoint = nativeBridge.getEntrypoint();

				// Get filtered setting IDs for WorkspaceUpdate category
				const filteredIds = getFilteredSettingIds({
					category: SettingsCategory.WorkspaceUpdate,
					platform,
					entrypoint,
				});

				// Build settings request only for available settings
				const settingsToLoad = [
					WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL,
					WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES,
					WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL_CONFIG,
				].filter((settingId) => filteredIds.includes(settingId));

				if (settingsToLoad.length === 0) {
					SettingsLogger.getInstance().write(
						logConstants.INFO,
						`No workspace update settings available for platform: ${platform}, entrypoint: ${entrypoint}`,
						AotCategory.Info,
						`No workspace update settings available for platform: ${platform}, entrypoint: ${entrypoint}`
					);
					return;
				}

				const settingsResponse = await nativeBridge.getSettings({
					settings: settingsToLoad.map((settingKey) => ({ settingKey })),
				});

				if (settingsResponse && settingsResponse.status === 0) {
					const getSettingsResp = settingsResponse as GetSettingsResponse;
					const settingsArray = getSettingsResp.data.settings || [];

					// Convert array to lookup object for easier access
					const settingsData = settingsArray.reduce(
						(
							acc: Record<
								string,
								{ settingKey: string; settingValue: unknown; enabled: boolean }
							>,
							setting: { settingKey: string; settingValue: unknown; enabled: boolean }
						) => {
							acc[setting.settingKey] = setting;
							return acc;
						},
						{} as Record<
							string,
							{ settingKey: string; settingValue: unknown; enabled: boolean }
						>
					);

					const notifyData = settingsData[WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES];
					if (
						notifyData?.settingValue !== undefined ||
						notifyData?.enabled !== undefined
					) {
						setNotifyUpdatesSetting((prev) => ({
							value:
								notifyData?.settingValue !== undefined
									? (notifyData.settingValue as boolean)
									: prev.value,
							disabled:
								notifyData?.enabled !== undefined ? !notifyData.enabled : prev.disabled,
						}));
					}

					const channelData = settingsData[WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL];
					if (
						channelData?.settingValue !== undefined ||
						channelData?.enabled !== undefined
					) {
						const channelValue =
							channelData?.settingValue !== undefined
								? (channelData.settingValue as string)
								: 'release';
						setUpdateChannelSetting((prev) => ({
							value: channelValue as WorkspaceUpdateChannel,
							disabled:
								channelData?.enabled !== undefined ? !channelData.enabled : prev.disabled,
						}));
						// Set the initial previous value to the loaded value
						setPreviousChannelValue(channelValue as WorkspaceUpdateChannel);
					}

					// Process channel config value
					const channelConfigData =
						settingsData[WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL_CONFIG];
					if (channelConfigData?.settingValue !== undefined) {
						const configValue = channelConfigData.settingValue as number;
						setChannelConfigValue(configValue);
					}
				}
			} catch (error) {
				const errorMessage = `Failed to load workspace update settings: ${error}`;
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					errorMessage,
					AotCategory.Error,
					errorMessage
				);
			}
		};

		loadWorkspaceUpdateSettings();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty dependency array - only run once on mount

	// Create handlers that use settingId directly
	const handleToggleChangeBySettingId = async (settingId: string, value: boolean) => {
		if (settingId === WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES) {
			setNotifyUpdatesSetting((prev) => ({ ...prev, value }));
			// Save to bridge
			try {
				const bridge = SettingsNativeBridgeFactory.getBridge();
				await bridge.setSetting({ settingKey: settingId, settingValue: value });
				SettingsLogger.getInstance().write(
					logConstants.INFO,
					`Successfully saved setting ${settingId} with value: ${value}`,
					AotCategory.Info,
					`Successfully saved setting ${settingId} with value: ${value}`
				);
			} catch (error) {
				const errorMessage = `Error saving ${settingId}: ${error}`;
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					errorMessage,
					AotCategory.Error,
					errorMessage
				);
				// Revert local state on error
				setNotifyUpdatesSetting((prev) => ({ ...prev, value: !value }));
			}
		} else {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Unknown toggle settingId: ${settingId}`
			);
		}
	};

	const handleRadioChangeBySettingId = async (settingId: string, value: string) => {
		if (settingId === WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL) {
			// Store the previous value before showing modal
			setPreviousChannelValue(updateChannelSetting.value);
			setPendingChannelValue(value as WorkspaceUpdateChannel);

			// Immediately update the visual state to show the new selection
			setUpdateChannelSetting((prev) => ({
				...prev,
				value: value as WorkspaceUpdateChannel,
			}));

			setShowChannelSwitchModal(true);
		} else {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Unknown radio settingId: ${settingId}`
			);
		}
	};

	const handleConfirmChannelSwitch = async () => {
		setShowChannelSwitchModal(false);
		// Save to bridge
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL,
				settingValue: pendingChannelValue,
			});

			// Explicitly set the state to the new value on successful save
			setUpdateChannelSetting((prev) => ({ ...prev, value: pendingChannelValue }));

			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Successfully saved setting ${WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL} with value: ${pendingChannelValue}`,
				AotCategory.Info,
				`Successfully saved setting ${WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL} with value: ${pendingChannelValue}`
			);
		} catch (error) {
			const errorMessage = `Error saving ${WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL}: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
			// Revert local state on error
			setUpdateChannelSetting((prev) => ({ ...prev, value: previousChannelValue }));
		}
	};

	const handleCancelChannelSwitch = () => {
		setShowChannelSwitchModal(false);
		// Revert to previous value
		setUpdateChannelSetting((prev) => ({ ...prev, value: previousChannelValue }));
	};

	return (
		<>
			<SettingsPage
				title={title}
				canNavigateBack={!!onNavigatingBack}
				onNavigatingBack={onNavigatingBack}
				settings={
					<>
						<ToggleSettingCard
							settingId={WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES}
							{...(getSettingConfig(
								WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES
							) as ToggleSettingConfig)}
							checked={notifyUpdatesSetting.value}
							disabled={notifyUpdatesSetting.disabled}
							onChange={(checked) =>
								handleToggleChangeBySettingId(
									WORKSPACE_UPDATE_SETTING_IDS.NOTIFY_UPDATES,
									checked
								)
							}
						>
							<PrimaryButton
								className="workspace-update-check-button"
								onClick={handleCheckForUpdates}
							>
								Check for Updates
							</PrimaryButton>
						</ToggleSettingCard>

						<RadioSetting
							settingId={WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL}
							{...(() => {
								const baseConfig = getSettingConfig(
									WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL
								) as RadioSettingConfig;
								const availableChannels = getAvailableChannels(channelConfigValue);
								// Filter options to only show available channels
								const filteredOptions =
									baseConfig.options?.filter((option) =>
										availableChannels.includes(option.value)
									) || [];

								return {
									...baseConfig,
									options: filteredOptions,
								};
							})()}
							selectedValue={updateChannelSetting.value}
							disabled={updateChannelSetting.disabled}
							onChange={(value) =>
								handleRadioChangeBySettingId(
									WORKSPACE_UPDATE_SETTING_IDS.UPDATE_CHANNEL,
									value
								)
							}
							showStatusOnValueChange={true}
							statusMessage={SETTING_NOTE_MESSAGES.RESTART_WORKSPACE_APP}
						/>
					</>
				}
			/>

			{/* Channel Switch Confirmation Modal */}
			<ModalDialog
				isOpen={showChannelSwitchModal}
				onClose={handleCancelChannelSwitch}
				type={ModalType.Info}
				title={getModalContent(pendingChannelValue).title}
				description={getModalContent(pendingChannelValue).description}
				primaryButton={{
					buttonText: 'Switch',
					onClick: handleConfirmChannelSwitch,
				}}
				secondaryButton={{
					buttonText: 'Cancel',
					onClick: handleCancelChannelSwitch,
				}}
			/>
		</>
	);
};
